import React from "react";
import { Box, Stack, Tooltip, Typography, alpha, useTheme } from "@mui/material";
import type { CohortFatigueSummary } from "../data/types";
import { tierColor } from "./primitives";

interface RecoveryStrainHeatmapProps {
  cohorts: CohortFatigueSummary[];
  onSelectCohort?: (cohortId: string) => void;
  height?: number;
}

// Readiness (x, 0–100, higher = better) vs Strain (y, 0–21, higher = more load).
// The top-left quadrant (low readiness + high strain) is the LRM hotspot.
const READINESS_MID = 50;
const STRAIN_MID = 13;
const STRAIN_MAX = 21;

/**
 * Recovery-vs-Strain quadrant scatter at cohort level. Each cohort is a point;
 * the low-readiness / high-strain quadrant is highlighted as the LRM hotspot.
 */
export const RecoveryStrainHeatmap: React.FC<RecoveryStrainHeatmapProps> = ({
  cohorts,
  onSelectCohort,
  height = 360,
}) => {
  const theme = useTheme();

  // Derive a point per cohort. Readiness ~ inverse of LRM; strain from CDI/FED mix.
  const points = cohorts.map((c) => {
    const readiness = Math.max(5, Math.min(95, 100 - c.lrmPct - c.fedPctWorkdays));
    const strain = Math.max(2, Math.min(STRAIN_MAX, 8 + c.cdiScore / 10 + c.fedPctWorkdays / 6));
    return { cohort: c, readiness, strain };
  });

  const xPct = (readiness: number) => (readiness / 100) * 100;
  const yPct = (strain: number) => (1 - strain / STRAIN_MAX) * 100;

  return (
    <Box>
      <Box
        role="img"
        aria-label="Recovery versus strain quadrant chart. Cohorts in the low-readiness, high-strain quadrant are load–readiness misalignment hotspots."
        sx={{
          position: "relative",
          height,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: "hidden",
          bgcolor: alpha(theme.palette.text.primary, 0.02),
        }}
      >
        {/* LRM hotspot quadrant: top-left (low readiness, high strain) */}
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            width: `${READINESS_MID}%`,
            height: `${(1 - STRAIN_MID / STRAIN_MAX) * 100}%`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.18)}, ${alpha(theme.palette.error.main, 0.04)})`,
          }}
        />
        <Typography
          variant="caption"
          sx={{
            position: "absolute",
            left: 8,
            top: 6,
            fontWeight: 700,
            color: theme.palette.error.main,
          }}
        >
          LRM hotspot · high load / low readiness
        </Typography>

        {/* Quadrant dividers */}
        <Box sx={{ position: "absolute", left: `${READINESS_MID}%`, top: 0, bottom: 0, borderLeft: `1px dashed ${theme.palette.divider}` }} />
        <Box sx={{ position: "absolute", top: `${(1 - STRAIN_MID / STRAIN_MAX) * 100}%`, left: 0, right: 0, borderTop: `1px dashed ${theme.palette.divider}` }} />

        {/* Points */}
        {points.map(({ cohort, readiness, strain }) => {
          const color = tierColor(cohort.riskTier, theme);
          const interactive = Boolean(onSelectCohort);
          return (
            <Tooltip
              key={cohort.cohort.id}
              arrow
              title={`${cohort.cohort.name} — readiness ${Math.round(readiness)}, strain ${strain.toFixed(1)}, LRM ${cohort.lrmPct}%`}
            >
              <Box
                role={interactive ? "button" : undefined}
                tabIndex={interactive ? 0 : undefined}
                aria-label={`${cohort.cohort.name}, risk ${cohort.riskTier}`}
                onClick={() => onSelectCohort?.(cohort.cohort.id)}
                onKeyDown={(e) => {
                  if (interactive && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    onSelectCohort?.(cohort.cohort.id);
                  }
                }}
                sx={{
                  position: "absolute",
                  left: `${xPct(readiness)}%`,
                  top: `${yPct(strain)}%`,
                  transform: "translate(-50%, -50%)",
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  bgcolor: color,
                  border: `2px solid ${theme.palette.background.paper}`,
                  boxShadow: `0 0 10px ${alpha(color, 0.7)}`,
                  cursor: interactive ? "pointer" : "default",
                  transition: "transform 0.15s ease",
                  "&:hover": interactive ? { transform: "translate(-50%, -50%) scale(1.25)" } : undefined,
                  "&:focus-visible": { outline: `2px solid ${color}`, outlineOffset: 2 },
                }}
              />
            </Tooltip>
          );
        })}
      </Box>

      {/* Axis labels */}
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Low readiness
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
          Readiness →
        </Typography>
        <Typography variant="caption" color="text.secondary">
          High readiness
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", textAlign: "center" }}>
        Vertical axis: load / strain (higher = more demand)
      </Typography>
    </Box>
  );
};
